import { useState, useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'plexus_banner_dismissed_v1';

function getTimeLeft() {
  const now = new Date();
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  const diff = endOfMonth.getTime() - now.getTime();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0 };
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { days, hours, minutes };
}

export default function AnnouncementBanner() {
  const [visible, setVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const updateHeight = useCallback(() => {
    if (nodeRef.current) {
      const h = nodeRef.current.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--banner-height', `${h}px`);
    }
  }, []);

  const bannerRef = (node: HTMLDivElement | null) => {
    nodeRef.current = node;
    if (node) {
      const h = node.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--banner-height', `${h}px`);
    }
  };

  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed) setVisible(true);
  }, []);

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.setProperty('--banner-height', '0px');
      return;
    }
    const interval = setInterval(() => setTimeLeft(getTimeLeft()), 60000);
    window.addEventListener('resize', updateHeight);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', updateHeight);
    };
  }, [visible, updateHeight]);

  const handleDismiss = () => {
    setVisible(false);
    sessionStorage.setItem(STORAGE_KEY, '1');
  };

  if (!visible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 left-0 right-0 z-[60] w-full"
      style={{
        background: 'linear-gradient(90deg, #0a0a0a 0%, #1a1200 40%, #0a0a0a 100%)',
        borderBottom: '1px solid rgba(251,191,36,0.25)',
      }}
    >
      <div className="relative flex items-center justify-center px-10 py-2.5 gap-4 flex-wrap">

        {/* Left accent line */}
        <span className="hidden sm:block w-6 h-px bg-amber-400/40 flex-shrink-0" />

        {/* Spark icon */}
        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
          <i className="ri-sparkling-2-fill text-amber-400 text-sm"></i>
        </span>

        {/* Message */}
        <p
          className="text-xs sm:text-sm text-white/90 tracking-wide text-center"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          <span className="text-amber-400 font-semibold">Free Consultation</span>
          {' '}this month only — limited spots available.
          {' '}
          <a
            href="/book-treatment"
            className="underline underline-offset-2 text-amber-300 hover:text-amber-200 transition-colors duration-200 font-medium cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Claim yours now
          </a>
        </p>

        {/* Countdown */}
        <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
          <span className="text-white/30 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>Ends in</span>
          {[
            { value: timeLeft.days, label: 'd' },
            { value: timeLeft.hours, label: 'h' },
            { value: timeLeft.minutes, label: 'm' },
          ].map(({ value, label }, i) => (
            <span key={i} className="flex items-center gap-0.5">
              <span
                className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-black bg-amber-400 min-w-[22px]"
                style={{ fontFamily: "'Inter', sans-serif", borderRadius: '3px' }}
              >
                {String(value).padStart(2, '0')}
              </span>
              <span className="text-white/40 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{label}</span>
              {i < 2 && <span className="text-amber-400/50 text-xs font-bold">:</span>}
            </span>
          ))}
        </div>

        {/* Right accent line */}
        <span className="hidden sm:block w-6 h-px bg-amber-400/40 flex-shrink-0" />

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-white/30 hover:text-amber-400 transition-colors duration-200 cursor-pointer flex-shrink-0"
          aria-label="Dismiss announcement"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>
    </div>
  );
}
